import { useState } from "react";
import { toast } from "sonner";
import { X, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useReference } from "@/context/ReferenceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SOURCE_TYPES = ["Direct / Self-Generated", "Referral", "LinkedIn", "Website",
  "Carrier Referral", "Social Media", "Other"];
const EMPTY = {
  client_name: "", phone: "", email: "", state: "", product_interest: "",
  source_type: "Direct / Self-Generated", source_detail: "", assigned_agent: "",
  next_action: "Initial contact call", next_action_date: new Date().toISOString().slice(0, 10),
  next_action_time: "", notes: "", lead_cost: "", attach_vendor_payment: false, vendor: "",
};

export const AddLeadDialog = ({ onClose, onSaved }) => {
  const { opts } = useReference();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (allowDuplicate = false) => {
    if (!f.client_name || !f.phone) { toast.error("Client name and phone are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/leads/internal", { ...f, allow_duplicate: allowDuplicate });
      if (data.duplicate && !allowDuplicate) {
        setDup(data.duplicate_of); setSaving(false); return;
      }
      toast.success(`Lead ${data.lead_display_id} added to pipeline${data.dnc ? " (⚠ on DNC list)" : ""}`);
      onSaved(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-lead-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[88vh] overflow-auto rounded-xl bg-card p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-1">
          <div className="font-heading text-lg font-bold">Add Lead</div>
          <button onClick={onClose}><X className="h-5 w-5 text-muted-foreground hover:text-foreground" /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Internal leads you enter go straight into the pipeline as <strong>New Accepted Lead</strong> — no vendor review needed.</p>

        {dup && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800" data-testid="dup-warning">
            <AlertTriangle className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
            Possible duplicate of <strong>{dup}</strong> (same phone/email). Continue anyway?
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => submit(true)} disabled={saving} data-testid="dup-continue">Add Anyway</Button>
              <Button size="sm" variant="outline" onClick={() => setDup(null)}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Client Name *</Label><Input value={f.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="al-name" /></div>
          <div><Label className="text-xs">Phone *</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="al-phone" /></div>
          <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div><Label className="text-xs">State</Label>
            <Select value={f.state} onValueChange={(v) => set("state", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(opts.states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Product Interest</Label>
            <Select value={f.product_interest} onValueChange={(v) => set("product_interest", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(opts.products || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Lead Source Type</Label>
            <Select value={f.source_type} onValueChange={(v) => set("source_type", v)}>
              <SelectTrigger data-testid="al-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Source Detail / Referral Name</Label><Input value={f.source_detail} onChange={(e) => set("source_detail", e.target.value)} /></div>
          <div><Label className="text-xs">Assigned Agent</Label>
            <Select value={f.assigned_agent} onValueChange={(v) => set("assigned_agent", v)}>
              <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
              <SelectContent>{(opts.agents || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Next Action</Label><Input value={f.next_action} onChange={(e) => set("next_action", e.target.value)} /></div>
          <div><Label className="text-xs">Next Action Date</Label><Input type="date" value={f.next_action_date} onChange={(e) => set("next_action_date", e.target.value)} /></div>
          <div><Label className="text-xs">Next Action Time</Label><Input value={f.next_action_time} onChange={(e) => set("next_action_time", e.target.value)} placeholder="e.g. 2:30 PM" /></div>
          <div className="col-span-2"><Label className="text-xs">Notes</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          <label className="col-span-2 flex items-center gap-2 text-[12px] text-muted-foreground">
            <input type="checkbox" checked={f.attach_vendor_payment} onChange={(e) => set("attach_vendor_payment", e.target.checked)} />
            Attach a vendor/source payment obligation to this lead
          </label>
          {f.attach_vendor_payment && (
            <div className="grid grid-cols-2 gap-3 col-span-2">
              <div><Label className="text-xs">Vendor / Source</Label><Input value={f.vendor} onChange={(e) => set("vendor", e.target.value)} /></div>
              <div><Label className="text-xs">Lead Cost ($)</Label><Input value={f.lead_cost} onChange={(e) => set("lead_cost", e.target.value)} /></div>
            </div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => submit(false)} disabled={saving} data-testid="al-save">{saving ? "Saving…" : "Add to Pipeline"}</Button>
        </div>
      </div>
    </div>
  );
};
